/**
 * The formatters every page uses. One module per repository, identical in both.
 *
 * The Portal team's formatter duplication is the cautionary tale this exists
 * to avoid: two pages formatting the same timestamp two ways is a support
 * conversation about whether the numbers match.
 */

/**
 * The wall clock in Berlin, seconds precision, with the zone written on it.
 *
 * DISPLAY ONLY. Everything stored, sent, logged and returned by the API stays
 * UTC, including /v1/health and every response body. A local time in a column
 * or on the wire is a mistake nobody can undo later, because the value looks
 * correct and no longer says what it is the time of.
 *
 * This used to render `toISOString()` with a trailing Z, and the reason given
 * was that an operator and an authority must compare the same string. That
 * reason is met here rather than abandoned: the zone travels with the value,
 * so "2026-08-29 13:09:23 CEST" names one instant and only one. What it drops
 * is the subtraction the reader was doing in their head, every time, to answer
 * the only question they actually had, which is how long ago.
 *
 * NOT AN OFFSET. `Europe/Berlin`, resolved per instant, because Berlin is CEST
 * until the last Sunday in October and CET after it. A `+2` written in code
 * keeps working for two months and then is silently an hour wrong, which is
 * precisely when somebody is reading timestamps to understand an incident.
 *
 * AND THE ZONE IS NOT DECORATION. On the morning of the switch, 00:30Z and
 * 01:30Z both render as 02:30. The abbreviation is the only thing that tells
 * them apart, because that hour genuinely happens twice.
 */
const BERLIN = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  // The 24 hour clock, stated rather than inherited from the locale.
  //
  // Not a tested guard, and the difference is worth writing down. Swapping
  // this for hour12: false changes nothing the suite can see: measured on this
  // runtime, en-GB and en-US both render midnight as 00 either way. It is here
  // so the intent survives somebody changing the locale, not because a failure
  // was observed. The first comment here claimed some ICU builds render 24,
  // which is a sentence no measurement of mine produced.
  hourCycle: 'h23',
  timeZoneName: 'short',
})

export function formatTimestamp(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return ''
  const date = typeof value === 'number' ? new Date(value * 1000) : new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  // Assembled from parts rather than taken as a formatted string, so the shape
  // stays year first and sortable at a glance down a column, which the locale
  // ordering would not be.
  const part: Record<string, string> = {}
  for (const { type, value: text } of BERLIN.formatToParts(date)) part[type] = text
  return (
    `${part.year}-${part.month}-${part.day} ` +
    `${part.hour}:${part.minute}:${part.second} ${part.timeZoneName}`
  )
}

/** How long ago, for freshness subtitles. Coarse on purpose: "3m ago" is what
 *  the reader needs, and a ticking seconds counter is movement without
 *  information. */
export function formatAge(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return ''
  const date = typeof value === 'number' ? new Date(value * 1000) : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000))
  if (seconds < 60) return `${seconds}s ago`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  return `${Math.floor(seconds / 86400)}d ago`
}

/** Two decimals, full stop separator, matching what the TIM contract carries.
 *  Formatted from the number as given; the service is the authority on the
 *  value and rounding it again here would invent a second answer. */
export function formatKwh(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return ''
  return value.toFixed(2)
}
